import { useState } from "react";
import { useParams, useLocation } from "wouter";
import { useSearch } from "wouter/use-browser-location";
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import AdminSmsAttempts from "@/components/AdminSmsAttempts";
import AdminSmsDiagnostics from "@/components/admin-sms-diagnostics";
import AdminAgentDetail from "@/components/AdminAgentDetail";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import AdminNav from "@/components/AdminNav";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  User, Mail, Calendar, Heart, MapPin, Settings,
  ArrowLeft, Clock, FileText, Trash2, CreditCard,
  ShieldCheck, Activity, FlaskConical, Pencil, Ban,
  Bell, Star, Phone, KeyRound,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import type { AccessSources } from "@shared/admin-agent";
import type { PlanTier } from "@shared/agent-plan";
import { investigationReturnHref } from "@/lib/paddle-investigation-navigation";
import { userListReturnHref } from "@/lib/admin-user-navigation";

interface AdminStatus { authenticated: boolean }

interface UserDetails {
  user: {
    id: string;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    profileImageUrl: string | null;
    isPro: boolean;
    effectiveTier: PlanTier;
    accessSources: AccessSources;
    paidPro: boolean;
    complimentaryPro: boolean;
    isTestAccount: boolean;
    isSuspended: boolean;
    billingProvider: string | null;
    whopMembershipId: string | null;
    stripeCustomerId: string | null;
    stripeSubscriptionId: string | null;
    billingMigrationState: string | null;
    createdAt: string;
    updatedAt: string;
  };
  favorites: Array<{
    id: number;
    name: string;
    city: string;
    state: string;
    country: string;
  }>;
  profile: {
    units: string;
    notifications: boolean;
    language: string;
    theme: string;
  } | null;
  billing: {
    provider: string | null;
    paidPro: boolean;
    complimentaryPro: boolean;
    isTestAccount: boolean;
    stripeCustomerId: string | null;
    stripeSubscriptionId: string | null;
    whopMembershipId: string | null;
    live: {
      provider: string;
      plan: string | null;
      subscriptionStatus: string | null;
      accessState: string;
      renewsAt: number | null;
      periodEndsAt: number | null;
      cancelAtPeriodEnd: boolean;
      providerState: string;
    } | null;
    error: string | null;
    recentOperations: Array<{
      id: number;
      operation: string;
      provider: string;
      status: string;
      eventId: string | null;
      objectId: string | null;
      code: string | null;
      resolvedAt: string | null;
      createdAt: string;
    }>;
  };
  stats: {
    favoritesCount: number;
    joinDate: string;
    lastActivity: string;
  };
  supportSummary?: {
    effectiveTier: string;
    suspended: boolean;
    sources: string[];
    storedBilling: {
      status: string | null; accessState: string | null; updatedAt: string | null;
      trialEndsAt: string | null; graceEndsAt: string | null; cancelAtPeriodEnd: boolean | null; periodEndsAt: string | null;
      readState?: "available" | "missing" | "unavailable";
    };
    observation: { state: "observed" | "unavailable" | "not_applicable"; observedAt: string | null; status: string | null; error: string | null };
  };
}

type Tab = 'overview' | 'favorites' | 'preferences' | 'audit' | 'danger';

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: 'overview',     label: 'Overview',     icon: <Activity className="h-4 w-4" />    },
  { id: 'favorites',    label: 'Favorites',    icon: <Heart className="h-4 w-4" />       },
  { id: 'preferences',  label: 'Preferences',  icon: <Settings className="h-4 w-4" />    },
  { id: 'audit',        label: 'Audit Log',    icon: <FileText className="h-4 w-4" />    },
  { id: 'danger',       label: 'Danger Zone',  icon: <Trash2 className="h-4 w-4" />      },
];

export default function AdminUserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const [, navigate] = useLocation();
  const search = useSearch();
  const billingReturnHref = investigationReturnHref(search);
  const usersReturnHref = userListReturnHref(search) ?? "/admin?view=users";
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const { toast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [sensitiveAction, setSensitiveAction] = useState<{ kind: "suspend" | "compPro" | "test"; value: boolean } | null>(null);
  const [sensitiveReason, setSensitiveReason] = useState("");
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const queryClient = useQueryClient();

  // Refresh both the user details and the audit timeline after admin mutations.
  const refreshUser = () => {
    refetchUser();
    queryClient.invalidateQueries({ queryKey: ['/api/admin/users', userId, 'audit'] });
    queryClient.invalidateQueries({ queryKey: ['/api/admin/users', userId, 'agent-admin'] });
    queryClient.invalidateQueries({ queryKey: ['/api/admin/agent-overview'] });
    queryClient.invalidateQueries({ queryKey: ['/api/admin/users'] });
  };

  // Check admin auth via session cookie
  const { data: adminStatus, isLoading: authLoading } = useQuery<AdminStatus>({
    queryKey: ['/api/admin/status'],
    retry: false,
    staleTime: 0,
  });

  // Fetch user details
  const { data: userDetails, isLoading: detailsLoading, isError: detailsError, refetch: refetchUser } = useQuery<UserDetails>({
    queryKey: [`/api/admin/users/${userId}`],
    enabled: !!userId && adminStatus?.authenticated === true,
  });

  // Grant / revoke test account access
  const testAccessMutation = useMutation({
    mutationFn: async ({ revoke, reason }: { revoke: boolean; reason: string }) => {
      const res = await fetch('/api/admin/grant-test-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId, revoke, confirm: true, reason }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message ?? 'Request failed');
      }
      return res.json();
    },
    onSuccess: (_, variables) => {
      setSensitiveAction(null);
      setSensitiveReason("");
      toast({
        title: variables.revoke ? 'Test access revoked' : 'Test access granted',
        description: variables.revoke
          ? 'Test access was removed. Effective access is recalculated from any remaining membership sources.'
          : 'User now has Agent access and phone verification bypassed.',
      });
      refreshUser();
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  // Delete user (cascade + Clerk)
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message ?? 'Failed to delete user');
      }
      // 204 = clean delete; 200 = deleted but with a warning payload
      if (res.status === 200) return res.json();
      return null;
    },
    onSuccess: () => {
      toast({ title: 'User deleted', description: 'The account and all its data have been permanently removed.' });
      navigate(usersReturnHref);
    },
    onError: (err: Error) => {
      toast({ title: 'Delete failed', description: err.message, variant: 'destructive' });
    },
  });

  // Suspend / unsuspend
  const suspendMutation = useMutation({
    mutationFn: async ({ suspend, reason }: { suspend: boolean; reason: string }) => {
      const res = await fetch(`/api/admin/users/${userId}/suspend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ suspend, confirm: true, reason }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message ?? 'Request failed');
      }
      return res.json();
    },
    onSuccess: (_, variables) => {
      setSensitiveAction(null);
      setSensitiveReason("");
      toast({
        title: variables.suspend ? 'Account suspended' : 'Account unsuspended',
        description: variables.suspend
          ? 'All API access is now blocked for this user.'
          : 'Access has been restored.',
      });
      refreshUser();
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  // Grant / revoke complimentary Pro
  const planOverrideMutation = useMutation({
    mutationFn: async ({ grantPro, reason }: { grantPro: boolean; reason: string }) => {
      const res = await fetch(`/api/admin/users/${userId}/plan-override`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ grantPro, confirm: true, reason }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message ?? 'Request failed');
      }
      return res.json();
    },
    onSuccess: (_, variables) => {
      setSensitiveAction(null);
      setSensitiveReason("");
      toast({
        title: variables.grantPro ? 'Pro plan granted' : 'Comp revoked',
        description: variables.grantPro
          ? 'User now has complimentary Pro access.'
          : 'Complimentary Pro access was removed. Effective access is recalculated from any remaining sources.',
      });
      refreshUser();
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  // Send password-reset email (admin-triggered)
  const [resetPasswordStatus, setResetPasswordStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);

  const resetPasswordMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/admin/users/${userId}/reset-password`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message ?? 'Request failed');
      }
      return res.json() as Promise<{ sent: boolean; email: string }>;
    },
    onSuccess: (data) => {
      setResetPasswordStatus('sent');
      setResetPasswordError(null);
      toast({ title: 'Reset email sent', description: `A sign-in link was emailed to ${data.email}.` });
    },
    onError: (err: Error) => {
      setResetPasswordStatus('error');
      setResetPasswordError(err.message);
      toast({ title: 'Failed to send reset email', description: err.message, variant: 'destructive' });
    },
  });

  // Resend the original welcome email with a fresh sign-in link.
  const [resendWelcomeStatus, setResendWelcomeStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [resendWelcomeError, setResendWelcomeError] = useState<string | null>(null);

  const resendWelcomeMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/admin/users/${userId}/resend-welcome`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message ?? 'Request failed');
      }
      return res.json() as Promise<{ sent: boolean }>;
    },
    onSuccess: () => {
      setResendWelcomeStatus('sent');
      setResendWelcomeError(null);
      toast({ title: 'Welcome email sent', description: `A new welcome email was sent to ${userDetails?.user.email}.` });
    },
    onError: (err: Error) => {
      setResendWelcomeStatus('error');
      setResendWelcomeError(err.message);
      toast({ title: 'Failed to resend welcome email', description: err.message, variant: 'destructive' });
    },
  });

  // Edit profile (name + email)
  const profileMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/admin/users/${userId}/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          firstName: editFirstName,
          lastName: editLastName,
          email: editEmail,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message ?? 'Request failed');
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Profile updated', description: 'Name and email saved to the database and Clerk.' });
      setEditingProfile(false);
      refreshUser();
    },
    onError: (err: Error) => {
      toast({ title: 'Update failed', description: err.message, variant: 'destructive' });
    },
  });

  const startEditingProfile = () => {
    if (!userDetails) return;
    setEditFirstName(userDetails.user.firstName ?? '');
    setEditLastName(userDetails.user.lastName ?? '');
    setEditEmail(userDetails.user.email ?? '');
    setEditingProfile(true);
  };

  const confirmSensitiveAction = () => {
    const reason = sensitiveReason.trim();
    if (!sensitiveAction || reason.length < 1 || reason.length > 500) return;
    if (sensitiveAction.kind === "suspend") suspendMutation.mutate({ suspend: sensitiveAction.value, reason });
    if (sensitiveAction.kind === "compPro") planOverrideMutation.mutate({ grantPro: sensitiveAction.value, reason });
    if (sensitiveAction.kind === "test") testAccessMutation.mutate({ revoke: sensitiveAction.value, reason });
  };
  const sensitivePending = suspendMutation.isPending || planOverrideMutation.isPending || testAccessMutation.isPending;
  const closeSensitiveDialog = (open: boolean) => {
    if (open || sensitivePending) return;
    setSensitiveAction(null);
    setSensitiveReason("");
  };

  const getPlanLabel = (u: UserDetails['user']) => {
    const tier = u.effectiveTier;
    return { label: tier === 'agent' ? 'Agent' : tier === 'pro' ? 'Pro' : 'Free',
      className: tier === 'agent' ? 'text-teal-600 font-semibold' : tier === 'pro' ? 'text-amber-500 font-semibold' : 'text-muted-foreground' };
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });

  const formatDateTime = (dateString: string) =>
    new Date(dateString).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  const getDisplayName = () => {
    if (!userDetails) return '';
    const { firstName, lastName, email } = userDetails.user;
    if (firstName || lastName) return `${firstName || ''} ${lastName || ''}`.trim();
    return email?.split('@')[0] ?? 'Member';
  };

  // ── Auth guard ────────────────────────────────────────────────────────────
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground text-sm">Checking access…</div>
      </div>
    );
  }

  if (!adminStatus?.authenticated) {
    navigate(billingReturnHref ?? '/admin');
    return null;
  }

  // ── Loading state ─────────────────────────────────────────────────────────
  const isLoading = detailsLoading;

  // ── Tab content ───────────────────────────────────────────────────────────
  const renderTab = () => {
    if (isLoading) {
      return (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
        </div>
      );
    }

    if (detailsError || !userDetails) {
      return (
        <Card>
          <CardContent className="p-6">
            <p role="alert" className="text-sm font-medium">Customer details are unavailable.</p>
            <p className="mt-1 text-sm text-muted-foreground">The account may have been removed or the request may have failed.</p>
            <Button type="button" variant="outline" size="sm" className="mt-4" onClick={() => void refetchUser()}>Retry customer details</Button>
          </CardContent>
        </Card>
      );
    }

    const user = userDetails!.user;

    switch (activeTab) {
      // ── Overview ──────────────────────────────────────────────────────────
      case 'overview':
        return (
          <div className="space-y-6">
            {userDetails!.supportSummary && (() => {
              const summary = userDetails!.supportSummary!;
              const stored = summary.storedBilling;
              const observation = summary.observation;
              const displayDate = (value: string | null) => value ? formatDateTime(value) : "—";
              return <Card className="overflow-hidden border-teal-900/20" data-testid="account-summary">
                <CardHeader className="bg-teal-50/50 dark:bg-teal-950/20">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div><CardTitle className="text-base">Account summary</CardTitle><p className="mt-1 text-xs text-muted-foreground">Fast support context · verify each source below before taking action.</p></div>
                    <Badge variant={summary.suspended ? "destructive" : "secondary"}>{summary.suspended ? "Application access suspended" : "Application access active"}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 p-4">
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div><p className="text-xs text-muted-foreground">Effective tier</p><p className="mt-1 font-semibold capitalize">{summary.effectiveTier}</p></div>
                    <div><p className="text-xs text-muted-foreground">Access sources</p><p className="mt-1 text-sm">{summary.sources.length ? summary.sources.map(source => ({ paidPro: "Paid Pro", paidAgent: "Paid Agent", complimentaryPro: "Complimentary Pro", complimentaryAgent: "Complimentary Agent", test: "Test" }[source] ?? source)).join(", ") : "None"}</p></div>
                    <div><p className="text-xs text-muted-foreground">{stored.readState === "unavailable" ? "Stored billing ledger · read unavailable" : stored.readState === "missing" ? "Stored billing ledger · no record" : "Stored billing ledger · verified record"}</p><p className="mt-1 text-sm">{stored.status ?? stored.accessState ?? (stored.readState === "unavailable" ? "Ledger could not be read" : stored.readState === "missing" ? "No stored billing record" : "No stored billing status")}</p><p className="text-xs text-muted-foreground">{stored.readState === "available" || !stored.readState ? `Updated ${displayDate(stored.updatedAt)}` : "No verified ledger timestamp"}{stored.cancelAtPeriodEnd === true ? " · scheduled to end" : ""}</p></div>
                  </div>
                  <div className={`rounded-lg border p-3 text-xs ${observation.state === "observed" ? "border-teal-800/15 bg-teal-50/50 dark:bg-teal-950/15" : "border-amber-700/20 bg-amber-50/60 dark:bg-amber-950/15"}`}>
                    <p className="font-semibold">{observation.state === "observed" ? "Recent provider read" : observation.state === "unavailable" ? "Recent provider read unavailable" : "Provider read not applicable"}</p>
                    <p className="mt-1">{observation.status ?? "No provider status"}{observation.observedAt ? ` · observed ${displayDate(observation.observedAt)}` : ""}</p>
                    {observation.error && <p className="mt-1 text-amber-800 dark:text-amber-200">{observation.error}</p>}
                    <p className="mt-1 text-muted-foreground">Provider observation is separate from the stored verified ledger. Suspension blocks application access; it does not change billing. Comp and test grants are access sources, not paid subscriptions.</p>
                  </div>
                  <dl className="grid grid-cols-2 gap-x-5 gap-y-2 text-xs sm:grid-cols-4">
                    <SummaryDatum label="Trial ends" value={displayDate(stored.trialEndsAt)} />
                    <SummaryDatum label="Grace ends" value={displayDate(stored.graceEndsAt)} />
                    <SummaryDatum label="Period ends" value={displayDate(stored.periodEndsAt)} />
                    <SummaryDatum label="Cancel at period end" value={stored.cancelAtPeriodEnd === null ? "Unavailable" : stored.cancelAtPeriodEnd ? "Yes" : "No"} />
                  </dl>
                  <div className="flex flex-wrap gap-2 border-t pt-3">
                    <Button size="sm" variant="outline" onClick={() => document.getElementById("billing-diagnostics")?.scrollIntoView({ behavior: "smooth", block: "start" })}>Billing details</Button>
                    <Button size="sm" variant="outline" onClick={() => document.getElementById("sms-diagnostics")?.scrollIntoView({ behavior: "smooth", block: "start" })}>SMS details</Button>
                    <Button size="sm" variant="outline" onClick={() => document.getElementById("agent-membership")?.scrollIntoView({ behavior: "smooth", block: "start" })}>Agent details</Button>
                    <Button size="sm" variant="outline" onClick={() => setActiveTab("audit")}>Audit log</Button>
                  </div>
                </CardContent>
              </Card>;
            })()}
            {/* Stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold">{userDetails!.stats.favoritesCount}</div>
                  <div className="text-xs text-muted-foreground mt-1">Saved Spots</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold">{userDetails!.profile?.units ?? '—'}</div>
                  <div className="text-xs text-muted-foreground mt-1">Units</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold capitalize">{userDetails!.profile?.theme ?? '—'}</div>
                  <div className="text-xs text-muted-foreground mt-1">Theme</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold">
                    {userDetails!.profile?.notifications ? (
                      <span className="text-green-600">On</span>
                    ) : (
                      <span className="text-muted-foreground">Off</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">Notifications</div>
                </CardContent>
              </Card>
            </div>

            {/* Account info */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4" /> Account Details
                  </CardTitle>
                  {!editingProfile && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={startEditingProfile}
                      data-testid="button-edit-profile"
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {editingProfile ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-muted-foreground font-medium">First name</label>
                        <Input
                          value={editFirstName}
                          onChange={(e) => setEditFirstName(e.target.value)}
                          className="mt-1"
                          data-testid="input-edit-first-name"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground font-medium">Last name</label>
                        <Input
                          value={editLastName}
                          onChange={(e) => setEditLastName(e.target.value)}
                          className="mt-1"
                          data-testid="input-edit-last-name"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="text-sm text-muted-foreground font-medium">Email</label>
                        <Input
                          type="email"
                          value={editEmail}
                          onChange={(e) => setEditEmail(e.target.value)}
                          className="mt-1"
                          data-testid="input-edit-email"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Changes sync to the user's Clerk sign-in account. The new email must not already be in use.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => profileMutation.mutate()}
                        disabled={profileMutation.isPending}
                        data-testid="button-save-profile"
                      >
                        {profileMutation.isPending ? 'Saving…' : 'Save'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingProfile(false)}
                        disabled={profileMutation.isPending}
                        data-testid="button-cancel-profile"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                  <div>
                    <dt className="text-muted-foreground font-medium">User ID</dt>
                    <dd className="font-mono text-xs mt-0.5 break-all">{user.id}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground font-medium">Email</dt>
                    <dd className="mt-0.5">{user.email}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground font-medium">Name</dt>
                    <dd className="mt-0.5">{[user.firstName, user.lastName].filter(Boolean).join(' ') || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground font-medium">Joined</dt>
                    <dd className="mt-0.5">{formatDateTime(user.createdAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground font-medium">Last Active</dt>
                    <dd className="mt-0.5">{formatDateTime(user.updatedAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground font-medium">Language</dt>
                    <dd className="mt-0.5">{userDetails!.profile?.language ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground font-medium">Plan</dt>
                    <dd className="mt-0.5 flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 ${getPlanLabel(user).className}`} data-testid="text-plan-status">
                        {user.isPro && '★ '}{getPlanLabel(user).label}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground font-medium">Status</dt>
                    <dd className="mt-0.5">
                      {user.isSuspended ? (
                        <Badge variant="destructive">Suspended</Badge>
                      ) : (
                        <span className="text-green-600">Active</span>
                      )}
                    </dd>
                  </div>
                </dl>
                )}
              </CardContent>
            </Card>

            <Card id="billing-diagnostics">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <CreditCard className="h-4 w-4" /> Billing Diagnostics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                  <div>
                    <dt className="text-muted-foreground font-medium">Paid provider</dt>
                    <dd className="mt-0.5 capitalize">{userDetails!.billing.provider ?? 'None'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground font-medium">Provider status</dt>
                    <dd className="mt-0.5">
                      {userDetails!.billing.live?.subscriptionStatus ??
                        userDetails!.billing.live?.accessState ??
                        'Not applicable'}
                      {userDetails!.billing.live?.providerState === 'cached' ? ' (cached)' : ''}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground font-medium">Plan</dt>
                    <dd className="mt-0.5 capitalize">{userDetails!.billing.live?.plan ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground font-medium">Renewal / period end</dt>
                    <dd className="mt-0.5">
                      {userDetails!.billing.live?.periodEndsAt
                        ? new Date(userDetails!.billing.live.periodEndsAt * 1000).toLocaleString()
                        : '—'}
                      {userDetails!.billing.live?.cancelAtPeriodEnd ? ' · Cancels then' : ''}
                    </dd>
                  </div>
                  <div className="sm:col-span-2 rounded-md border border-muted bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                    The following Stripe and Whop identifiers are historical records only. Live billing inspection uses Paddle; these identifiers and any old migration metadata do not provide management or purchase actions.
                  </div>
                  <div>
                    <dt className="text-muted-foreground font-medium">Stripe customer ID · historical only</dt>
                    <dd className="mt-0.5 font-mono text-xs break-all">
                      {userDetails!.billing.stripeCustomerId ?? '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground font-medium">Stripe subscription ID · historical only</dt>
                    <dd className="mt-0.5 font-mono text-xs break-all">
                      {userDetails!.billing.stripeSubscriptionId ?? '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground font-medium">Whop membership ID · historical only</dt>
                    <dd className="mt-0.5 font-mono text-xs break-all">
                      {userDetails!.billing.whopMembershipId ?? '—'}
                    </dd>
                  </div>
                </dl>
                {userDetails!.billing.error && (
                  <div className="rounded-md border border-red-300/60 bg-red-50 p-3 text-red-900 dark:bg-red-950/20 dark:text-red-100">
                    {userDetails!.billing.error}
                  </div>
                )}
                <div>
                  <div className="font-medium mb-2">Recent billing operations</div>
                  {userDetails!.billing.recentOperations.length === 0 ? (
                    <p className="text-muted-foreground">No billing operations recorded for this user.</p>
                  ) : (
                    <div className="divide-y rounded-md border">
                      {userDetails!.billing.recentOperations.slice(0, 8).map((operation, index) => (
                        <div key={`${operation.createdAt}-${index}`} className="px-3 py-2 text-xs">
                          <div className="font-medium">
                            {operation.provider} · {operation.operation}
                            {operation.code ? ` · ${operation.code}` : ''}
                            {operation.resolvedAt ? ' · resolved' : ''}
                          </div>
                          <div className="text-muted-foreground">
                            {new Date(operation.createdAt).toLocaleString()}
                            {operation.eventId ? ` · event ${operation.eventId}` : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

      <div id="agent-membership"><AdminAgentDetail userId={user.id} /></div>

      <div id="sms-diagnostics">
        <AdminSmsDiagnostics key={user.id} userId={user.id} />
      </div>

            {/* Test account access */}
            <Card className={user.isTestAccount ? 'border-amber-500/40 bg-amber-500/5' : ''}>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FlaskConical className="h-4 w-4" /> Test Account Access
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="text-sm text-muted-foreground max-w-sm">
                    {user.isTestAccount ? (
                      <p>
                        This account has <span className="text-amber-500 font-medium">test Agent access</span>: Agent and Pro features are unlocked and alert-form phone verification is bypassed. Actual Agent SMS still requires a verified recipient. Use this for App Store / Play Store review.
                      </p>
                    ) : (
                      <p>
                        Grant test Agent access with alert-form phone verification bypassed. Actual Agent SMS still requires a verified recipient. Intended for App Store and Play Store review.
                      </p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant={user.isTestAccount ? 'outline' : 'default'}
                    disabled={testAccessMutation.isPending}
                    onClick={() => { setSensitiveReason(""); setSensitiveAction({ kind: "test", value: user.isTestAccount }); }}
                    className="shrink-0"
                  >
                    {testAccessMutation.isPending
                      ? 'Saving…'
                      : user.isTestAccount
                        ? 'Revoke test access'
                        : 'Grant test access'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      // ── Favorites ─────────────────────────────────────────────────────────
      case 'favorites':
        return (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Heart className="h-4 w-4 text-rose-500" />
                Saved Surf Spots ({userDetails!.favorites.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {userDetails!.favorites.length > 0 ? (
                <div className="divide-y">
                  {userDetails!.favorites.map((spot) => (
                    <div key={spot.id} className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-3">
                        <MapPin className="h-4 w-4 text-blue-500 shrink-0" />
                        <span className="font-medium">{spot.name}</span>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {spot.city}, {spot.state || spot.country}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm py-4">No favorite spots saved yet.</p>
              )}
            </CardContent>
          </Card>
        );

      // ── Preferences ───────────────────────────────────────────────────────
      case 'preferences':
        return (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Settings className="h-4 w-4" /> App Preferences
              </CardTitle>
            </CardHeader>
            <CardContent>
              {userDetails!.profile ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
                  {[
                    { label: 'Units',         value: userDetails!.profile.units       },
                    { label: 'Language',       value: userDetails!.profile.language    },
                    { label: 'Theme',          value: userDetails!.profile.theme       },
                    { label: 'Notifications',  value: userDetails!.profile.notifications ? 'Enabled' : 'Disabled' },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex items-center justify-between border rounded-lg px-4 py-3">
                      <span className="text-muted-foreground font-medium">{label}</span>
                      <Badge variant="secondary" className="capitalize">{value}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm py-4">No preferences recorded for this user.</p>
              )}
            </CardContent>
          </Card>
        );

      // ── Audit Log ─────────────────────────────────────────────────────────
      case 'audit':
        return <AuditLogTab userId={userId!} />;

      // ── Danger Zone (placeholder) ─────────────────────────────────────────
      case 'danger':
        return (
          <div className="space-y-4">
            {/* Password reset — not destructive, shown above the danger card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <KeyRound className="h-4 w-4" /> Password Reset
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-start justify-between gap-4">
                  <div className="text-sm text-muted-foreground max-w-sm">
                    <p>
                      Send this user a one-time sign-in link so they can access their account and set a new password.
                      The link expires in 24 hours and can only be used once.
                    </p>
                    {resetPasswordStatus === 'sent' && (
                      <p className="mt-2 text-green-600 font-medium">
                        ✓ Sign-in link sent to {user.email}
                      </p>
                    )}
                    {resetPasswordStatus === 'error' && resetPasswordError && (
                      <p className="mt-2 text-destructive text-xs">{resetPasswordError}</p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0"
                    disabled={resetPasswordMutation.isPending || resetPasswordStatus === 'sent'}
                    onClick={() => {
                      setResetPasswordStatus('sending');
                      resetPasswordMutation.mutate();
                    }}
                    data-testid="button-reset-password"
                  >
                    <KeyRound className="h-3.5 w-3.5 mr-1.5" />
                    {resetPasswordMutation.isPending
                      ? 'Sending…'
                      : resetPasswordStatus === 'sent'
                        ? 'Email sent'
                        : 'Send password reset email'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Mail className="h-4 w-4" /> Welcome Email
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-start justify-between gap-4">
                  <div className="text-sm text-muted-foreground max-w-sm">
                    <p>
                      Send the account welcome email again with a fresh sign-in link. This is useful if the original email was missed or filtered as spam.
                    </p>
                    {resendWelcomeStatus === 'sent' && (
                      <p className="mt-2 text-green-600 font-medium">
                        ✓ Welcome email sent to {user.email}
                      </p>
                    )}
                    {resendWelcomeStatus === 'error' && resendWelcomeError && (
                      <p className="mt-2 text-destructive text-xs">{resendWelcomeError}</p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0"
                    disabled={resendWelcomeMutation.isPending}
                    onClick={() => {
                      setResendWelcomeStatus('sending');
                      resendWelcomeMutation.mutate();
                    }}
                    data-testid="button-resend-welcome"
                  >
                    <Mail className="h-3.5 w-3.5 mr-1.5" />
                    {resendWelcomeMutation.isPending ? 'Sending…' : 'Resend welcome email'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-destructive/40">
              <CardHeader>
                <CardTitle className="text-base text-destructive flex items-center gap-2">
                  <Trash2 className="h-4 w-4" /> Danger Zone
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Delete user */}
                <div className="flex items-center justify-between rounded-lg border border-destructive/30 bg-destructive/5 p-4">
                  <div>
                    <p className="font-medium text-sm">Delete account</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Permanently remove this user and all their data. Cannot be undone.
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={deleteMutation.isPending}
                    onClick={() => setDeleteDialogOpen(true)}
                    data-testid="button-delete-user"
                  >
                    {deleteMutation.isPending ? 'Deleting…' : 'Delete user'}
                  </Button>
                </div>

                {/* Suspend */}
                <div className={`flex items-center justify-between rounded-lg border p-4 ${user.isSuspended ? 'border-red-500/40 bg-red-500/5' : ''}`}>
                  <div>
                    <p className="font-medium text-sm flex items-center gap-2">
                      <Ban className="h-3.5 w-3.5 text-muted-foreground" />
                      {user.isSuspended ? 'Account suspended' : 'Suspend account'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {user.isSuspended
                        ? 'All API access is blocked for this user. Unsuspend to restore access immediately.'
                        : 'Block access without deleting data. User can be re-enabled at any time.'}
                    </p>
                  </div>
                  <Button
                    variant={user.isSuspended ? 'default' : 'outline'}
                    size="sm"
                    disabled={suspendMutation.isPending}
                    onClick={() => { setSensitiveReason(""); setSensitiveAction({ kind: "suspend", value: !user.isSuspended }); }}
                    data-testid="button-toggle-suspend"
                  >
                    {suspendMutation.isPending
                      ? 'Saving…'
                      : user.isSuspended ? 'Unsuspend' : 'Suspend'}
                  </Button>
                </div>

                {/* Plan override (comp) */}
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="flex items-start gap-3">
                    <CreditCard className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">
                        Plan override — current: <span className="font-semibold">{getPlanLabel(user).label}</span>
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Only complimentary Pro access changes. Paid Agent/Pro, complimentary Agent,
                        test access, subscriptions and alert settings are preserved.
                      </p>
                    </div>
                  </div>
                  <Button
                    variant={user.complimentaryPro ? 'outline' : 'default'}
                    size="sm"
                    disabled={planOverrideMutation.isPending}
                    onClick={() => { setSensitiveReason(""); setSensitiveAction({ kind: "compPro", value: !user.complimentaryPro }); }}
                    data-testid="button-plan-override"
                  >
                    {planOverrideMutation.isPending
                      ? 'Saving…'
                      : user.complimentaryPro ? 'Revoke comp Pro' : 'Grant comp Pro'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Delete confirmation dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete {getDisplayName()}?</AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div className="space-y-3 text-sm text-muted-foreground">
                      <p>
                        This will permanently delete all their alerts, favorites, and data,
                        and remove their sign-in account so they cannot log back in.
                        This cannot be undone.
                      </p>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() => deleteMutation.mutate()}
                    data-testid="button-confirm-delete"
                  >
                    Delete permanently
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminNav
        activeSection="user-detail"
        onLogout={() => navigate('/admin')}
      />

      <div className="md:pl-60 pb-24 md:pb-6">
        <div className="container mx-auto max-w-4xl px-4 py-6">

          {/* Back + breadcrumb */}
          <button
            onClick={() => navigate(usersReturnHref)}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Users
          </button>
          {billingReturnHref && (
            <button
              type="button"
              onClick={() => navigate(billingReturnHref)}
              className="mb-6 ml-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
              data-testid="back-to-billing-investigation"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to billing investigation
            </button>
          )}

          {/* User header */}
          <div className="flex items-center gap-4 mb-8">
            {isLoading ? (
              <>
                <Skeleton className="h-16 w-16 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-6 w-40" />
                  <Skeleton className="h-4 w-56" />
                </div>
              </>
            ) : userDetails ? (
              <>
                {userDetails!.user.profileImageUrl ? (
                  <img
                    src={userDetails!.user.profileImageUrl}
                    alt="Avatar"
                    className="h-16 w-16 shrink-0 rounded-full object-cover ring-2 ring-border"
                  />
                ) : (
                  <div className="h-16 w-16 shrink-0 rounded-full bg-muted flex items-center justify-center ring-2 ring-border">
                    <User className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}
                <div className="min-w-0">
                  <h1 className="text-2xl font-bold flex flex-wrap items-center gap-2">
                    <span className="min-w-0 break-all">{getDisplayName()}</span>
                    {userDetails!.user.isSuspended && (
                      <Badge variant="destructive" data-testid="badge-suspended">Suspended</Badge>
                    )}
                  </h1>
                  <p className="text-muted-foreground flex items-center gap-1.5 mt-0.5">
                    <Mail className="h-3.5 w-3.5 shrink-0" />
                    <span className="min-w-0 break-all">{userDetails!.user.email}</span>
                  </p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1">
                    <Calendar className="h-3 w-3" />
                    Joined {formatDate(userDetails!.user.createdAt)}
                  </p>
                </div>
              </>
            ) : (
              <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3">
                <p className="text-sm font-medium">Customer details unavailable</p>
                <p className="mt-1 text-xs text-muted-foreground">This account may have been removed or the request failed.</p>
              </div>
            )}
          </div>

          {/* Tab bar */}
          <div className="flex gap-1 border-b mb-6 overflow-x-auto">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                  ${activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'}`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {renderTab()}
          <Dialog open={!!sensitiveAction} onOpenChange={closeSensitiveDialog}>
            <DialogContent className="max-h-[85dvh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {sensitiveAction?.kind === "suspend"
                    ? sensitiveAction.value ? "Suspend this account?" : "Restore application access?"
                    : sensitiveAction?.kind === "compPro"
                      ? sensitiveAction.value ? "Grant complimentary Pro?" : "Revoke complimentary Pro?"
                      : sensitiveAction?.value ? "Revoke test access?" : "Grant test access?"}
                </DialogTitle>
                <DialogDescription>
                  {sensitiveAction?.kind === "suspend"
                    ? "This changes application access only. It does not change billing, cancel a subscription, or charge the customer."
                    : sensitiveAction?.kind === "compPro"
                      ? "Only the complimentary Pro source changes. Other access sources are preserved; billing and subscription status are unchanged."
                      : "Only the test access source changes. Granting test access bypasses alert-form phone verification; actual Agent SMS still requires a verified recipient. Billing is unchanged."}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <label htmlFor="sensitive-action-reason" className="text-sm font-medium">Reason <span className="text-destructive">*</span></label>
                <Input id="sensitive-action-reason" value={sensitiveReason} onChange={event => setSensitiveReason(event.target.value)} maxLength={500} disabled={sensitivePending} placeholder="Record why this change is needed" />
                <p className="text-xs text-muted-foreground">Required for the audit record. {sensitiveReason.trim().length}/500 characters.</p>
              </div>
              {[
                suspendMutation.error, planOverrideMutation.error, testAccessMutation.error,
              ].some(Boolean) && <p role="alert" className="text-sm text-destructive">
                {(suspendMutation.error ?? planOverrideMutation.error ?? testAccessMutation.error)?.message}
              </p>}
              <DialogFooter>
                <Button variant="outline" disabled={sensitivePending} onClick={() => closeSensitiveDialog(false)}>Cancel</Button>
                <Button disabled={sensitivePending || sensitiveReason.trim().length < 1 || sensitiveReason.trim().length > 500} onClick={confirmSensitiveAction}>
                  {sensitivePending ? "Saving…" : "Confirm change"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <AdminSmsAttempts userId={userId} />
        </div>
      </div>
    </div>
  );
}

// ── Audit Log tab ───────────────────────────────────────────────────────────

interface AuditEvent {
  type: string;
  timestamp: string;
  description: string;
  meta?: Record<string, unknown>;
}

interface AuditResponse {
  events: AuditEvent[];
  total: number;
  page: number;
  pageSize: number;
}

const AUDIT_ICONS: Record<string, React.ReactNode> = {
  account_created: <User className="h-4 w-4 text-blue-600" />,
  alert_created:   <Bell className="h-4 w-4 text-emerald-600" />,
  alert_updated:   <Bell className="h-4 w-4 text-amber-600" />,
  alert_fired:     <Bell className="h-4 w-4 text-red-500" />,
  spot_favorited:  <MapPin className="h-4 w-4 text-pink-500" />,
  pro_granted:     <Star className="h-4 w-4 text-yellow-500" />,
  pro_revoked:     <Star className="h-4 w-4 text-muted-foreground" />,
  phone_verified:  <Phone className="h-4 w-4 text-indigo-500" />,
};

function AuditLogTab({ userId }: { userId: string }) {
  const pageSize = 20;

  // True sequential pagination: each "Load more" fetches the next page and
  // appends it. Keyed as ['/api/admin/users', userId, 'audit'] so admin
  // mutations can invalidate the whole audit history by prefix.
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<AuditResponse>({
    queryKey: ['/api/admin/users', userId, 'audit'],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const res = await fetch(`/api/admin/users/${userId}/audit?page=${pageParam}&pageSize=${pageSize}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
    getNextPageParam: (lastPage) =>
      lastPage.page * lastPage.pageSize < lastPage.total ? lastPage.page + 1 : undefined,
  });

  const events = data?.pages.flatMap((p) => p.events) ?? [];
  const total = data?.pages[0]?.total ?? 0;
  const hasMore = hasNextPage ?? false;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4" /> Audit Log
          {data && (
            <Badge variant="secondary" className="ml-1">{total} events</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4 py-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex items-start gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
            ))}
          </div>
        ) : isError ? (
          <p className="text-sm text-destructive py-4">Failed to load audit log.</p>
        ) : events.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
            <FileText className="h-10 w-10 text-muted-foreground/40" />
            <p className="font-medium text-muted-foreground">No activity recorded yet</p>
          </div>
        ) : (
          <>
            <ol className="relative space-y-0" data-testid="audit-timeline">
              {events.map((event, i) => (
                <li
                  key={`${event.type}-${event.timestamp}-${i}`}
                  className="relative flex items-start gap-3 pb-6 last:pb-0"
                  data-testid={`audit-event-${i}`}
                >
                  {/* vertical connector */}
                  {i < events.length - 1 && (
                    <span className="absolute left-4 top-8 bottom-0 w-px bg-border" aria-hidden />
                  )}
                  <span className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-background">
                    {AUDIT_ICONS[event.type] ?? <FileText className="h-4 w-4 text-muted-foreground" />}
                  </span>
                  <div className="min-w-0 pt-1">
                    <p className="text-sm font-medium leading-snug">{event.description}</p>
                    <p
                      className="text-xs text-muted-foreground mt-0.5"
                      title={format(new Date(event.timestamp), 'PPpp')}
                    >
                      {formatDistanceToNow(new Date(event.timestamp), { addSuffix: true })}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
            {hasMore && (
              <div className="pt-4 flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  data-testid="button-load-more-audit"
                >
                  {isFetchingNextPage ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function SummaryDatum({ label, value }: { label: string; value: string }) {
  return <div><dt className="text-muted-foreground">{label}</dt><dd className="mt-0.5 font-medium">{value}</dd></div>;
}
